import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Stripe Imports
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import backendURL, { stripe_publishable_key } from "../../config"; // Assuming config exports these

// MUI Components for Confirmation Dialog (Optional but recommended for better UX)
import { Typography, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button as MuiButton, Box, CircularProgress, Alert } from '@mui/material'; // Use MuiButton to avoid naming conflict
import { useNavigate } from 'react-router-dom';

// --- Configuration ---
const API_BASE_URL = backendURL;
const STRIPE_PKEY = stripe_publishable_key;

if (!STRIPE_PKEY || STRIPE_PKEY === 'pk_test_YOUR_PUBLISHABLE_KEY') {
    console.warn("Stripe Publishable Key is not set or is using the default placeholder in PlanSelectionPage.js.");
}
const stripePromise = STRIPE_PKEY ? loadStripe(STRIPE_PKEY) : Promise.resolve(null);

// --- Inline Helper Functions ---
const formatCurrency = (amount, currencyCode = 'usd') => { /* ... (keep implementation) ... */
    if (typeof amount !== 'number') return '';
    try {
         return new Intl.NumberFormat('en-US', {
             style: 'currency',
             currency: currencyCode?.toUpperCase() || 'USD',
         }).format(amount);
    } catch(e) {
         console.warn("Currency formatting failed:", e);
         return `$${amount.toFixed(2)}`; // Fallback with dollar sign
    }
 };

// --- Inline Simple Components (Using MUI now for consistency) ---
const LoadingSpinner = ({ size = 'medium' }) => { // Adjusted size values
    const muiSize = size === 'small' ? 20 : size === 'large' ? 40 : 30;
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: size === 'small' ? 0.2 : 1 }}>
            <CircularProgress size={muiSize} />
        </Box>
    );
}

const ErrorMessage = ({ message }) => {
    if (!message) return null;
    return (
        <Alert severity="error" sx={{ my: 2 }}>
            {typeof message === 'object' ? JSON.stringify(message) : message}
        </Alert>
    );
};


// --- Plan Card Component (Modified to use MUI Button if desired, but keeping custom style for now) ---
const PlanCard = ({ plan, onSelectPlan, isCurrentPlan, isLoading }) => { /* ... (keep implementation, maybe switch button to MuiButton later) ... */
    const { id, display_name, price, currency, interval, trial_days, features } = plan || {};
    if (!plan) return null;
    const handleSelect = () => { if (onSelectPlan && !isCurrentPlan && !isLoading) { onSelectPlan(plan); } };
    const featureList = [ /* ... feature logic ... */
        features.web_pages_limit === -1 ? 'Unlimited Web Pages' : features.web_pages_limit && `${features.web_pages_limit} Web Pages`,
        features.document_limit === -1 ? 'Unlimited Documents' : features.document_limit && `${features.document_limit} Documents`,
        features.llm_tokens_in_limit === -1 ? 'Unlimited LLM Tokens' : features.llm_tokens_in_limit && `${(features.llm_tokens_in_limit / 1000000).toFixed(1)}M In Tokens`,
        features.llm_tokens_out_limit === -1 ? '' : features.llm_tokens_out_limit && `${(features.llm_tokens_out_limit / 1000000).toFixed(1)}M Out Tokens`,
        features.multilingual && 'Multilingual Support', features.google_calendar && 'Google Calendar',
        features.ecommerce && 'E-commerce Features', features.ai_agents && 'AI Agents Access'
    ].filter(Boolean);
    const cardStyle = { border: `2px solid ${isCurrentPlan ? '#4CAF50' : '#ddd'}`, padding: '20px', margin: '10px', borderRadius: '8px', minWidth: '260px', maxWidth: '320px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 2px 5px rgba(0,0,0,0.1)', background: '#fff' };
    const buttonStyle = { padding: '10px 15px', cursor: (isCurrentPlan || isLoading) ? 'not-allowed' : 'pointer', width: '100%', background: isCurrentPlan ? '#e0e0e0' : '#007bff', color: isCurrentPlan ? '#555' : '#fff', border: 'none', borderRadius: '4px', fontSize: '1em', fontWeight: 'bold', minHeight: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center', opacity: isLoading ? 0.7 : 1, transition: 'background-color 0.2s ease' };

    return (
        <div style={cardStyle}>
            <div>
                <h3 style={{ marginTop: 0, marginBottom: '10px', color: '#333' }}>{display_name}</h3>
                <p style={{ fontSize: '1.6em', fontWeight: 'bold', margin: '5px 0', minHeight: '2.5em', color: '#007bff' }}>
                    {price > 0 ? `${formatCurrency(price, currency)} / ${interval}` : 'Free'}
                </p>
                {trial_days > 0 && <p style={{ minHeight: '1.5em', color: '#555', fontStyle: 'italic' }}>{trial_days}-day free trial available</p>}
                <ul style={{ listStyle: 'none', paddingLeft: 0, marginTop: '15px', fontSize: '0.9em', color: '#555', minHeight: '80px' }}>
                    {featureList.map((feature, index) => (<li key={index} style={{ marginBottom: '5px' }}>✓ {feature}</li>))}
                    {featureList.length === 0 && <li>Basic Features</li>}
                </ul>
             </div>
             <div style={{ marginTop: 'auto' }}>
                 <button onClick={handleSelect} disabled={isCurrentPlan || isLoading} style={buttonStyle}>
                     {isLoading ? <LoadingSpinner size="small"/> : (isCurrentPlan ? 'Current Plan' : 'Select Plan')}
                 </button>
                 {isCurrentPlan && <p style={{ color: 'green', marginTop: '8px', textAlign: 'center', fontSize: '0.9em', fontWeight: 'bold' }}>✓ Your active plan</p>}
            </div>
        </div>
    );
};


// --- Internal Component for Stripe Checkout Form (Adding New Card) ---
const InternalCheckoutForm = ({ planToSubscribe, clientSecret, onSuccess, onCancel, onError }) => { /* ... (keep implementation, maybe style buttons with MUI) ... */
    const stripe = useStripe();
    const elements = useElements();
    const [message, setMessage] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const navigate = useNavigate();
    // API Call to subscribe *with a newly setup* Payment Method ID
    const callCreateSubscriptionWithCardAPI = async (planId, paymentMethodId) => {
        const token = localStorage.getItem('access_token');
        const headers = { 'Content-Type': 'application/json', ...(token && { 'Authorization': `Bearer ${token}` }) };
        const body = { plan_id: planId, gateway_payment_method_id: paymentMethodId };
        console.log("Calling POST /subscriptions with NEW card", body);
        return axios.post(`${API_BASE_URL}/subscriptions`, body, { headers });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!stripe || !elements) { setMessage("Stripe not loaded."); if (onError) onError("Stripe not loaded."); return; }
        setIsProcessing(true); setMessage(null);

        // Confirm the SetupIntent to get the PaymentMethod ID
        const { error: setupError, setupIntent } = await stripe.confirmSetup({
            elements, confirmParams: {}, redirect: 'if_required',
        });

        if (setupError) {
            const errorMsg = `Payment setup failed: ${setupError.message}`;
            setMessage(errorMsg); setIsProcessing(false); if (onError) onError(errorMsg);
            return;
        }

        if (setupIntent?.status === 'succeeded') {
            const paymentMethodId = setupIntent.payment_method;
            if (!paymentMethodId) {
                const errorMsg = "Payment method details missing after setup.";
                setMessage(errorMsg); setIsProcessing(false); if (onError) onError(errorMsg);
                return;
            }
            // Now that SetupIntent succeeded and we have the PM ID, call backend to create sub
            try {
                console.log(`SetupIntent success. PM ID: ${paymentMethodId}. Calling backend to subscribe...`);
                setMessage("Saving card and creating subscription...");
                const response = await callCreateSubscriptionWithCardAPI(planToSubscribe.id, paymentMethodId);

                if (response.data && response.status < 300) {
                    setMessage(`Subscribed to ${planToSubscribe.display_name}!`);
                    // Update local storage user object
                    try {
                        const userStr = localStorage.getItem('user');
                        if (userStr) {
                            const userData = JSON.parse(userStr);
                            userData.subscription = response.data;
                            if (userData.subscription && userData.subscription?.plan?.price > 0){
                                userData.has_used_trial = true
                            }
                            localStorage.setItem('user', JSON.stringify(userData));
                        }
                    } catch (e) { console.error("Error updating user in local storage:", e); }

                    // Notify parent component via onSuccess prop
                     setTimeout(() => { // Delay allows user to see success message briefly
                        if (onSuccess) onSuccess(response.data); // Call parent onSuccess
                     }, 1500);

                } else {
                    throw new Error(response.data?.message || `Subscription failed with status ${response.status}`);
                }
            } catch (error) {
                console.error("Backend sub call failed after card setup:", error);
                const errorMsg = error.response?.data?.message || error.message || "Subscription creation failed after card setup.";
                setMessage(errorMsg); setIsProcessing(false); if (onError) onError(errorMsg);
            }
        } else {
            const errorMsg = `Card setup status: ${setupIntent?.status}. Please try again or use a different card.`;
            setMessage(errorMsg); setIsProcessing(false); if (onError) onError(errorMsg);
        }
    };
    let userData = null;
    const userStr = localStorage.getItem('user');
    if (userStr) {
        userData = JSON.parse(userStr);
        if (userData && !userData.hasOwnProperty('has_used_trial')) {
            userData.has_used_trial = false
        }
        localStorage.setItem('user', JSON.stringify(userData));
    }
    const paymentElementOptions = { layout: "tabs" };
    // Message styling using MUI Alert now
    // Button styling - use MUI buttons
    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ p: 3, backgroundColor: 'background.paper', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>Add Payment Method for {planToSubscribe.display_name}</Typography>
            <PaymentElement id="payment-element" options={paymentElementOptions} />
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                {planToSubscribe.price > 0 ?
                    (planToSubscribe.trial_days > 0 && userData && !userData.has_used_trial
                        ? `Your ${planToSubscribe.trial_days}-day trial starts now. Charge of ${formatCurrency(planToSubscribe.price, planToSubscribe.currency)} / ${planToSubscribe.interval} after trial.`
                        : `Charge of ${formatCurrency(planToSubscribe.price, planToSubscribe.currency)} / ${planToSubscribe.interval} starting today.`
                    )
                    : 'This plan is free. Adding card for potential future upgrades or charges.'}
                <br/> We'll securely save this card for future use.
            </Typography>
            {message && (
                 <Alert
                    severity={message.toLowerCase().includes('fail') || message.toLowerCase().includes('error') ? "error" : "info"}
                    sx={{ mt: 2 }}
                 >
                    {message}
                 </Alert>
            )}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <MuiButton type="button" onClick={onCancel} disabled={isProcessing} color="secondary">
                    Cancel
                </MuiButton>
                <MuiButton
                    type="submit"
                    disabled={isProcessing || !stripe || !elements}
                    variant="contained"
                    startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {isProcessing ? 'Processing...' : 'Save Card & Subscribe'}
                </MuiButton>
            </Box>
        </Box>
    );
};


// ========================================================================
// --- Main Plan Selection Page Component ---
// ========================================================================
const PricingPage = ({ onComplete, onError }) => {
    // State for data
    const [plans, setPlans] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null);

    const navigate = useNavigate();

    // State for loading and errors
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [initialError, setInitialError] = useState(null); // For page load errors
    const [actionError, setActionError] = useState(null);   // For errors during subscription actions
    const [isActionLoading, setIsActionLoading] = useState(false); // General loading for button clicks etc.

    // State for flow control
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [showBasicOptions, setShowBasicOptions] = useState(false); // For 'Basic' plan choice
    const [showStripeForm, setShowStripeForm] = useState(false); // Show add card form
    const [stripeClientSecret, setStripeClientSecret] = useState(null); // For Stripe Elements
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false); // Confirm using default card


    
    const updateLocalStorageSubscription = (subscriptionData) => {
        try {
           const userStr = localStorage.getItem('user');
           if (userStr) {
               const userData = JSON.parse(userStr);
               userData.subscription = subscriptionData;
               if (userData.subscription && userData.subscription?.plan?.price > 0){
                   userData.has_used_trial = true
               }
               setCurrentUser(userData);
               localStorage.setItem('user', JSON.stringify(userData));
               console.log("Local storage user subscription updated.");
           } else {
                console.warn("User data not found in local storage for update.");
           }
        } catch (e) {
           console.error("Error updating user subscription in local storage:", e);
        }
   };


    // --- Authentication & Headers ---
    // Note: Assumes token is present. Critical auth failures handled by caller or higher-level component.
    const getAuthHeaders = useCallback(() => {
        const token = localStorage.getItem('access_token');
        const userStr = localStorage.getItem('user');
        if (userStr) {
            const userData = JSON.parse(userStr);
            setCurrentUser(userData);
        }
       
        if (!token) {
            console.error("PlanSelectionPage: Auth token not found.");
            // Signal error up immediately if no token when headers are needed
            if (onError) onError("Authentication required. Please log in again.");
            return null;
        }
        return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
    }, [onError]); // Added onError dependency

    // --- Data Fetching ---
    const fetchInitialData = useCallback(async () => {
        setIsInitialLoading(true);
        setInitialError(null); setActionError(null); // Clear previous errors
        // Reset flow states
        setSelectedPlan(null); setShowBasicOptions(false); setShowStripeForm(false);
        setStripeClientSecret(null); setShowConfirmationDialog(false);
        setPaymentMethods([]); setDefaultPaymentMethod(null); // Reset payment method state

        const headers = getAuthHeaders();
        // Don't proceed if headers couldn't be created (error already signaled by getAuthHeaders)
        if (!headers) {
            setIsInitialLoading(false);
            setInitialError("Authentication token is missing."); // Set local error for display
            return;
        }

        try {
            const plansPromise = axios.get(`${API_BASE_URL}/plans`);
            // Only fetch subscription and payment methods if authenticated (headers exist)
            const subPromise = axios.get(`${API_BASE_URL}/subscriptions/current`, { headers });
            const pmPromise = axios.get(`${API_BASE_URL}/payment-methods`, { headers });

            const [plansResult, subResult, pmResult] = await Promise.allSettled([
                plansPromise, subPromise, pmPromise
            ]);

            // Process Plans
            if (plansResult.status === 'fulfilled' && plansResult.value.data) {
                setPlans(plansResult.value.data);
            } else {
                console.error("Plan fetch failed:", plansResult.reason);
                // Allow page to load if plans fail, but show error
                setInitialError(plansResult.reason?.response?.data?.message || "Could not load available plans.");
            }

            // Process Current Subscription
            if (subResult.status === 'fulfilled' && subResult.value.data) {
                updateLocalStorageSubscription(subResult.value.data);
                setCurrentSubscription(subResult.value.data);
                window.location.href = window.location.href = "/Dashboard";
            } else if (subResult.reason?.response?.status === 404) { // Handle 404 as potentially 'freemium' or no sub
                setCurrentSubscription(null);
            } else if (subResult.reason?.response?.status === 401) {
                 // Handle session expiry specifically if needed, though getAuthHeaders should catch future attempts
                 console.error("Subscription fetch failed (401):", subResult.reason);
                 setInitialError("Your session may have expired. Please log in again.");
                 if (onError) onError("Session expired."); // Signal critical error up
            } else {
                console.error("Subscription fetch failed:", subResult.reason);
                // Show non-critical error if sub fails but plans load
                setInitialError(prev => prev ? `${prev} Also failed to load current subscription.` : "Failed to load current subscription details.");
            }

            // Process Payment Methods
            if (pmResult.status === 'fulfilled' && pmResult.value.data) {
                console.log(pmResult)
                const methods = pmResult.value.data || [];
                setPaymentMethods(methods);
                const defaultMethod = methods.find(pm => pm.is_default);
                if (defaultMethod) {
                    setDefaultPaymentMethod(defaultMethod);
                    console.log("Default payment method found:", defaultMethod);
                } else {
                    console.log("No default payment method found.");
                }
            } else if (pmResult.reason?.response?.status === 401) {
                 console.error("Payment methods fetch failed (401):", pmResult.reason);
                 setInitialError(prev => prev ? `${prev} Session may have expired.` : "Your session may have expired. Please log in again.");
                 if (onError) onError("Session expired."); // Signal critical error up
            }
            else {
                // Non-critical error, proceed without payment methods if needed
                console.warn("Payment methods fetch failed:", pmResult.reason);
            }

        } catch (err) { // Catch unexpected errors during setup
            console.error("Error during initial data fetch setup:", err);
            setInitialError(err.message || "An unexpected error occurred while loading page data.");
            if (onError) onError(err.message); // Signal up
        } finally {
            setIsInitialLoading(false);
        }
    }, [getAuthHeaders, onError]); // Added getAuthHeaders, onError

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]); // Fetch data on mount

    // --- Helper Functions ---
    const isPlanPaid = (plan) => plan && plan.price > 0;

    // --- Subscription Action: Using Existing Default Card ---
    const subscribeWithDefaultCard = useCallback(async () => {
        if (!selectedPlan || !defaultPaymentMethod || isActionLoading) return;

        setShowConfirmationDialog(false); // Close dialog
        setIsActionLoading(true); setActionError(null);
        const headers = getAuthHeaders();
        if (!headers) { setIsActionLoading(false); return; } // Error already handled

        console.log(`Subscribing to ${selectedPlan.name} using default PM: ${defaultPaymentMethod.id}`);
        try {
            // *** IMPORTANT: Backend needs to handle this ***
            // Assuming POST /subscriptions without 'gateway_payment_method_id'
            // implies using the customer's default source.
            // OR, explicitly send the default PM ID if backend requires it:
            // const body = { plan_id: selectedPlan.id, gateway_payment_method_id: defaultPaymentMethod.id };
            const body = (defaultPaymentMethod) ?  { plan_id: selectedPlan.id , gateway_payment_method_id: defaultPaymentMethod.gateway_id }: { plan_id: selectedPlan.id }; // Assuming backend uses default

            const response = await axios.post(`${API_BASE_URL}/subscriptions`, body, { headers });

            if (response.status < 300 && response.data) {
                console.log("Subscription successful using default card:", response.data);
                updateLocalStorageSubscription(response.data);
                setSelectedPlan(null); // Reset selection
                if (onComplete) onComplete(`Successfully subscribed to ${response.data.plan?.display_name || selectedPlan.display_name}!`); // Signal completion
            } else {
                throw new Error(response.data?.message || `Subscription failed with status ${response.status}`);
            }
        } catch (err) {
            console.error("Subscription with default card failed:", err);
            const errorMsg = err.response?.data?.message || "Subscription failed using the default payment method.";
            
            setShowConfirmationDialog(true); // Set local error for display
            setShowStripeForm(false); // Set local error for display
            setShowBasicOptions(false); // Set local error for display
            setActionError(errorMsg); // Set local error for display
            // if (onError) onError(errorMsg); // Signal error up
        } finally {
            setIsActionLoading(false);
        }
    }, [selectedPlan, defaultPaymentMethod, isActionLoading, getAuthHeaders, onComplete, onError]);

    // --- Subscription Action: For plans NOT requiring payment (Freemium, Basic w/o card) ---
    const subscribeDirectly = useCallback(async (planToSubscribe) => {
        if (!planToSubscribe || isActionLoading) return;
        setIsActionLoading(true); setActionError(null);
        const headers = getAuthHeaders();
        if (!headers) { setIsActionLoading(false); return; }

        console.log(`Subscribing directly (no PM) to ${planToSubscribe.name}...`);
        try {
            const response = await axios.post(`${API_BASE_URL}/subscriptions`, { plan_id: planToSubscribe.id }, { headers });
            if (response.status < 300 && response.data) {
                 console.log("Direct subscription successful:", response.data);
                 updateLocalStorageSubscription(response.data);
                 setSelectedPlan(null); // Reset selection
                 setShowBasicOptions(false);
                 if (onComplete) onComplete(`Successfully subscribed to ${response.data.plan?.display_name || planToSubscribe.display_name}!`); // Signal completion
            } else {
                throw new Error(response.data?.message || `Subscription failed with status ${response.status}`);
            }
        } catch (err) {
            console.error("Direct subscription failed:", err);
            const errorMsg = err.response?.data?.message || "Subscription failed.";
            setActionError(errorMsg);
            if (onError) onError(errorMsg);
        } finally {
            setIsActionLoading(false);
        }
    }, [isActionLoading, getAuthHeaders, onComplete, onError]);

    // --- Action: Initiate Adding a New Card via Stripe Form ---
    const initiateCardAddition = useCallback(async (planToSubscribe) => {
        if (!planToSubscribe || isActionLoading) return;

        setShowConfirmationDialog(false); // Ensure confirmation dialog is closed
        setIsActionLoading(true); setActionError(null); setStripeClientSecret(null);
        const headers = getAuthHeaders();
        if (!headers) { setIsActionLoading(false); return; }

        console.log(`Initiating card addition for ${planToSubscribe.name}...`);
        try {
            // Get SetupIntent client secret from backend
            const response = await axios.post(`${API_BASE_URL}/payment-methods/setup-intent`, {}, { headers });
            if (response.data?.client_secret) {
                setStripeClientSecret(response.data.client_secret);
                setShowBasicOptions(false); // Hide basic options if open
                setShowStripeForm(true); // Show stripe form
                console.log("Setup Intent received.");
            } else {
                throw new Error("Client secret not received from setup intent.");
            }
        } catch (err) {
            console.error("Setup Intent fetch failed:", err);
            const errorMsg = err.response?.data?.message || "Could not initialize payment method setup.";
            setActionError(errorMsg);
            if (onError) onError(errorMsg);
        } finally {
            setIsActionLoading(false); // Loading was for fetching intent, form has its own loading
        }
    }, [isActionLoading, getAuthHeaders, onError]);

    // --- Handle Plan Card Selection Click ---
    const handleSelectPlan = useCallback((plan) => {
        if (isActionLoading || plan.id === currentSubscription?.plan?.id) return;

        console.log("Selected Plan:", plan.name);
        setSelectedPlan(plan);
        setActionError(null); // Clear previous errors on new selection
        setShowBasicOptions(false);
        setShowStripeForm(false);
        setStripeClientSecret(null);
        setShowConfirmationDialog(false);

        const planNameLower = plan.name?.toLowerCase();
        if (!isPlanPaid(plan)) { // Includes Freemium
            subscribeDirectly(plan);
        } else if (planNameLower === 'basic' && !defaultPaymentMethod) { // Basic plan allows choice
             setShowBasicOptions(true);
        } else { // Other Paid plans (Premium, Enterprise, etc.)
            if (defaultPaymentMethod) {
                // User has a default card, ask for confirmation
                setShowConfirmationDialog(true);
            } else {
                // No default card, must add one
                initiateCardAddition(plan);
            }
        }
    }, [isActionLoading, currentSubscription, defaultPaymentMethod, subscribeDirectly, initiateCardAddition]);


    // --- Handlers for Basic Plan Options ---
    const handleBasicOptionClick = (addCard) => {
        if (!selectedPlan || isActionLoading) return;
        setShowBasicOptions(false); // Hide options after choice
        if (addCard) {
            initiateCardAddition(selectedPlan); // Start flow to add card
        } else {
            if(currentUser?.has_used_trial){
                setActionError("You have already used the trial. Now you need to add a card or use the freemium plan");
                setShowBasicOptions(true); // Hide options after choice
            }else{
                subscribeDirectly(selectedPlan); // Subscribe without adding card
            }
        }
    };

    // --- Handlers for Confirmation Dialog ---
    const handleConfirmDialogClose = () => {
        if (isActionLoading) return; // Prevent closing while action is in progress
        setShowConfirmationDialog(false);
        setSelectedPlan(null); // Cancel selection if dialog is closed without confirming
    };

    const handleConfirmDialogConfirm = () => {
        // Trigger subscription using the default card
        subscribeWithDefaultCard();
    };

    const handleConfirmDialogUseDifferentCard = () => {
        // Close dialog and initiate adding a new card
        setShowConfirmationDialog(false);
        initiateCardAddition(selectedPlan);
    };


    // --- Callbacks for Stripe Form (InternalCheckoutForm) ---
    const handleCheckoutSuccess = useCallback((backendSubscriptionData) => {
        console.log("Stripe Checkout + Backend Sub successful:", backendSubscriptionData);
        setSelectedPlan(null); setShowStripeForm(false); setStripeClientSecret(null); setActionError(null);
        // Local storage update is handled within InternalCheckoutForm now
        if (onComplete) onComplete(`Successfully subscribed to ${backendSubscriptionData.plan?.display_name || 'the selected plan'}!`); // Signal completion
    }, [onComplete]);

    const handleCancelCheckout = useCallback(() => {
        console.log(selectedPlan,showStripeForm,showConfirmationDialog,showBasicOptions)
        console.log("Checkout cancelled.");
        setShowConfirmationDialog(false);
        setShowBasicOptions(false)
        setSelectedPlan(null); setShowStripeForm(false); setStripeClientSecret(null); setActionError(null); setIsActionLoading(false);setShowBasicOptions(false);
        // No need to call onError here, it's a user cancellation
    }, []);

     const handleCheckoutError = useCallback((errorMessage) => {
         console.error("Error received from InternalCheckoutForm:", errorMessage);
         // Error is displayed within the form itself. Optionally set parent error too.
         // setActionError(errorMessage || "An error occurred during payment processing.");
         // Don't automatically close the form, let user see the error
         setIsActionLoading(false); // Ensure loading state is reset if form initiated it

         // Signal error up via onError prop (already done inside InternalCheckoutForm)
         // if(onError) onError(errorMessage); // No need to call again here
     }, []);


    // --- Render Logic ---
    if (isInitialLoading) {
        return <Box sx={{ p: 5, textAlign: 'center' }}><LoadingSpinner size="large" /></Box>;
    }

    // Display initial load errors more prominently if plans didn't load
    if (initialError && !plans.length) {
        return <ErrorMessage message={`Failed to load page: ${initialError}`} />;
    }

    const currentPlanId = currentSubscription?.plan?.id;
    const stripeOptions = stripeClientSecret ? { clientSecret: stripeClientSecret, appearance: { theme: 'stripe' } } : null;

    return (
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: '1200px', mx: 'auto' }}>
            {/* Use Typography for heading */}
            <Typography variant="h4" component="h2" sx={{ textAlign: 'center', mb: 4 }}>
                Choose Your Plan
            </Typography>

            {/* General Action Error (for errors outside the Stripe form or during confirmations) */}
            {actionError && !showStripeForm && <ErrorMessage message={actionError} />}
            {/* Initial load error if plans *did* load but other data failed */}
            {initialError && plans.length > 0 && <ErrorMessage message={initialError} />}


            {/* --- Display Plan Cards --- */}
            {!selectedPlan && !showStripeForm && !showConfirmationDialog && !showBasicOptions && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2 }}>
                    {plans.length > 0 ? (
                        plans.map(plan => (
                            <PlanCard
                                key={plan.id} plan={plan} onSelectPlan={handleSelectPlan}
                                isCurrentPlan={plan.id === currentPlanId}
                                isLoading={isActionLoading && selectedPlan?.id === plan.id} // Loading shown on card during API calls before dialog/form
                            />
                        ))
                    ) : ( !isInitialLoading && !initialError && <Typography sx={{ textAlign: 'center', my: 3 }}>No plans available.</Typography> )}
                </Box>
            )}

             {/* --- Basic Plan Options --- */}
             {showBasicOptions && selectedPlan?.name?.toLowerCase() === 'basic' && !isActionLoading && !defaultPaymentMethod && (
                 <Box sx={{ maxWidth: '500px', margin: '40px auto', p: 3, border: '1px solid #007bff', borderRadius: 2, textAlign: 'center', bgcolor: '#f8f9fa' }}>
                     <Typography variant="h6" gutterBottom>Subscribe to {selectedPlan.display_name}</Typography>
                     <Typography sx={{ mb: 3 }}>Would you like to add a payment method now for potential future use? (Optional)</Typography>
                     <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                         {/* Using MUI Buttons */}
                         <MuiButton onClick={() => handleBasicOptionClick(true)} variant="contained" color="success">
                             Yes, Add Card Now
                         </MuiButton>
                         <MuiButton onClick={() => handleBasicOptionClick(false)} variant="contained" color="primary">
                             No, Start Basic Plan
                         </MuiButton>
                     </Box>
                     <MuiButton onClick={handleCancelCheckout} sx={{ mt: 3, textTransform: 'none' }} color="secondary">
                        Cancel Selection
                     </MuiButton>
                 </Box>
             )}

             {/* --- Confirmation Dialog for Using Default Card --- */}
             {selectedPlan && defaultPaymentMethod && (
                <Dialog
                    open={showConfirmationDialog}
                    onClose={handleConfirmDialogClose} // Allow closing by clicking outside
                    aria-labelledby="confirm-subscription-dialog-title"
                >
                    <DialogTitle id="confirm-subscription-dialog-title">Confirm Subscription</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Subscribe to the <strong>{selectedPlan.display_name}</strong> plan
                            ({formatCurrency(selectedPlan.price, selectedPlan.currency)} / {selectedPlan.interval})
                            using your saved card: <br />
                            <strong>{defaultPaymentMethod.brand?.toUpperCase()} ending in {defaultPaymentMethod.last4}</strong>?
                        </DialogContentText>
                        {/* Display action error specific to this flow if needed */}
                        {actionError && <ErrorMessage message={actionError} />}
                    </DialogContent>
                    <DialogActions sx={{ p:2 }}>
                        <MuiButton onClick={handleConfirmDialogClose} color="secondary" disabled={isActionLoading}>
                            Cancel
                        </MuiButton>
                        <MuiButton onClick={handleConfirmDialogUseDifferentCard} disabled={isActionLoading}>
                            Use Different Card
                        </MuiButton>
                        <MuiButton onClick={handleConfirmDialogConfirm} variant="contained" color="primary" disabled={isActionLoading}
                           startIcon={isActionLoading ? <CircularProgress size={20} color="inherit"/> : null} >
                            {isActionLoading ? 'Processing...' : 'Confirm Subscription'}
                        </MuiButton>
                    </DialogActions>
                </Dialog>
             )}


            {/* Loading indicator during subscription actions (before form/dialog shows) */}
            {isActionLoading && !showStripeForm && !showConfirmationDialog && !showBasicOptions && (
                 <Box sx={{ textAlign: 'center', my: 4 }}><LoadingSpinner /> <Typography>Processing...</Typography></Box>
            )}


            {/* --- Stripe Checkout Form Section (for adding NEW card) --- */}
            {showStripeForm && selectedPlan && stripeOptions && stripePromise && (
                 // Use Box for consistent layout/styling
                 <Box sx={{ maxWidth: '550px', margin: '40px auto', border: '1px solid #ccc', borderRadius: 2, overflow: 'hidden', background: '#f9f9f9', boxShadow: 3 }}>
                    {/* Display actionError specifically for setup intent fetch failure if needed */}
                    {/* {actionError && <ErrorMessage message={actionError} /> } */}
                    <Elements stripe={stripePromise} options={stripeOptions}>
                        <InternalCheckoutForm
                            planToSubscribe={selectedPlan}
                            clientSecret={stripeClientSecret}
                            onSuccess={handleCheckoutSuccess} // Calls parent onComplete
                            onCancel={handleCancelCheckout}   // Resets local state
                            onError={handleCheckoutError}    // Errors shown inside form, also calls parent onError
                        />
                    </Elements>
                 </Box>
             )}
        </Box>
    );
};

export default PricingPage;